//Dependencies............................................
import { Tabs, Dropdown, Button, Modal, Input, Tooltip, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';

//Components..............................................
import DeleteModal from "../../../../GlobalComponents/Modals/DeleteModal";
import { Path, childArrayKey, setAtPath, deleteAtPath } from "../../../../../utils/conditionTree";

const conditionItems = [
    { label: 'AND', key: 'and' },
    { label: 'OR', key: 'or' },
    { label: 'Rule', key: 'rule' },
];

const ruleTypeTag = (type: 'CAN' | 'LIN') => (
    <Tag color={type === 'LIN' ? 'purple' : 'blue'} style={{ marginLeft: 4, marginRight: 0 }}>{type}</Tag>
);

function RecursiveDropdown({
    depth,
    path,
    condition,
    formContext,
    root,
    onChange,
    }   :   {
    depth: number,
    path: Path,
    condition: any,
    formContext: any,
    root: any,
    onChange: (newRoot: any) => void,
    })
{
    const selectedKey: 'and' | 'or' | 'rule' | null =
        'and' in condition ? 'and' : 'or' in condition ? 'or' : ('rule' in condition ? 'rule' : null);

    const ruleArray: any[] = formContext.rules || [];
    const children: any[] = condition[selectedKey === 'and' ? 'and' : 'or'] || [];

    const handleSelect = ({ key }: { key: string }) => {
        if (key === 'and' || key === 'or') {
            onChange(setAtPath(root, path, () => ({ [key]: [{}, {}] })));
        } else if (key === 'rule') {
            // `rule: null` means "leaf type chosen, no specific rule picked
            // yet" - distinct from a real (numeric) selection, and matches
            // how an incomplete CAN/LIN rule elsewhere in this app shows as
            // invalid until filled in, rather than silently guessing one.
            onChange(setAtPath(root, path, () => ({ rule: null })));
        }
    };

    const handleRuleSelect = (index: number) => {
        onChange(setAtPath(root, path, () => ({ rule: index })));
    };

    const handleAddChild = () => {
        onChange(setAtPath(root, path, (node: any) => {
            const key = childArrayKey(node);
            return { ...node, [key]: [...node[key], {}] };
        }));
    };

    const handleAddSibling = () => {
        onChange(setAtPath(root, path.slice(0, -1), (node: any) => {
            const key = childArrayKey(node);
            return { ...node, [key]: [...node[key], {}] };
        }));
    };

    const handleDeleteSelf = () => {
        onChange(deleteAtPath(root, path));
    };

    const getButtonType = (key: string | null) => {
        switch (key) {
            case 'and': return 'blue';
            case 'or': return 'cyan';
            case 'rule': return 'pink';
        }
    };

    return (
        <div style={{ marginLeft: depth * 20 }}>
            <Dropdown
                menu={{ items: conditionItems, onClick: handleSelect }}
                trigger={['click']}
            >
                <Button color={getButtonType(selectedKey)} variant='outlined'>
                    {selectedKey ? selectedKey.toUpperCase() : 'Select'}
                </Button>
            </Dropdown>
            {depth > 0 && (
                <Button onClick={handleDeleteSelf} color="red" variant='outlined'>-</Button>
            )}
            {depth > 0 && (
                <Button onClick={handleAddSibling} color="green" variant='outlined'>+</Button>
            )}
            {selectedKey === 'rule' && ruleArray.length > 0 && (
                <Dropdown
                    menu={{
                        items: ruleArray.map((r: any, i: number) => ({
                            label: <span>{i}: {r.description} {ruleTypeTag(r.type === 'LIN' ? 'LIN' : 'CAN')}</span>,
                            key: String(i),
                        })),
                        onClick: (e) => handleRuleSelect(Number(e.key)),
                    }}
                    trigger={['click']}
                >
                    <Button color={getButtonType(selectedKey)} variant='outlined'>
                        {condition.rule !== null && condition.rule !== undefined
                            ? <>{condition.rule}: {ruleArray[condition.rule]?.description ?? ''}</>
                            : 'Select'}
                        {condition.rule !== null && condition.rule !== undefined &&
                            ruleTypeTag(ruleArray[condition.rule]?.type === 'LIN' ? 'LIN' : 'CAN')}
                    </Button>
                </Dropdown>
            )}
            {(selectedKey === 'and' || selectedKey === 'or') && (
                <Button onClick={handleAddChild} color="green" variant='outlined'>+</Button>
            )}
            <div>
                {children.map((subCondition, index) => (
                    <RecursiveDropdown
                        key={index}
                        depth={depth + 1}
                        path={[...path, index]}
                        condition={subCondition}
                        formContext={formContext}
                        root={root}
                        onChange={onChange}
                    />
                ))}
            </div>
        </div>
    );
}

export default  function Conf30FunctionalityControlField({
    onChange,
    formData,
    formContext,
    }   :   {
    formData: any,
    onChange: (newFormData: any) => void,
    formContext: any
    })
{
    const [activeKey, setActiveKey] = useState<string | null>(null);

    const [isEditing, setIsEditing] = useState(false);
    const [editingTabKey, setEditingTabKey] = useState<string | null>(null);
    const [newTabLabel, setNewTabLabel] = useState<string>('');

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteFunction, setDeleteFunction] = useState<string | null>(null);
    const [selectedTabName, setSelectedTabName] = useState<string | null>(null);

    const tabKeys: string[] = formData ? Object.keys(formData) : [];

    // Keep activeKey pointing at a tab that still exists - e.g. after a
    // rename or delete changes the key set - defaulting to the first tab.
    useEffect(() => {
        if (tabKeys.length === 0) {
            setActiveKey(null);
        } else if (!activeKey || !tabKeys.includes(activeKey)) {
            setActiveKey(tabKeys[0]);
        }
    }, [formData]);// eslint-disable-line react-hooks/exhaustive-deps

    const removeTab = (key: string) => {
        const updatedData: { [key: string]: any } = { ...formData };
        delete updatedData[key];
        onChange(updatedData);
    };

    const handleAddTab = () => {
        let i = tabKeys.length + 1;
        let newKey = `Tab-${i}`;
        while (formData[newKey]) {
            i += 1;
            newKey = `Tab-${i}`;
        }
        onChange({ ...formData, [newKey]: {} });
        setActiveKey(newKey);
    };

    const showRenameModal = (key: string) => {
        setEditingTabKey(key);
        setNewTabLabel(key);
        setIsEditing(true);
    };

    const handleRenameTab = () => {
        if (editingTabKey && newTabLabel && newTabLabel !== editingTabKey) {
            const updatedData: { [key: string]: any } = { ...formData };
            updatedData[newTabLabel] = updatedData[editingTabKey];
            delete updatedData[editingTabKey];
            onChange(updatedData);
            setActiveKey(newTabLabel);
        }
        setIsEditing(false);
        setEditingTabKey(null);
        setNewTabLabel('');
    };

    const items = tabKeys.map(propertyName => ({
        key: propertyName,
        label: (
            <span>
                <>
                    <Tooltip title="Rename Tab">
                            <EditOutlined
                                onClick={() => showRenameModal(propertyName)}
                                style={{ cursor: 'pointer', marginLeft: 5 }}
                            />
                    </Tooltip>
                    {'  '}{propertyName}
                </>
            </span>
        ),
        children: (
            <RecursiveDropdown
                depth={0}
                path={[]}
                condition={formData[propertyName] || {}}
                formContext={formContext}
                root={formData[propertyName] || {}}
                onChange={(newCondition: any) => onChange({ ...formData, [propertyName]: newCondition })}
            />
        ),
        forceRender: true,
    }));

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <Button
                    onClick={ () => {
                        setDeleteFunction('clearAll');
                        setShowDeleteModal(true);
                    }}
                    style={{ marginRight: '10px' }}
                >
                    Clear All
                </Button>
                <Button
                    onClick={() => {
                        setDeleteFunction('clearActiveTab');
                        setShowDeleteModal(true);
                    }}
                >
                    Clear Active Tab
                </Button>
            </div>
            <Tabs
                type="editable-card"
                onEdit={(key, action) => {
                    if (action === 'add') {
                        handleAddTab();
                    } else if (action === 'remove') {
                        setSelectedTabName(key as string);
                        setDeleteFunction('deleteTab');
                        setShowDeleteModal(true);
                    }
                }}
                activeKey={activeKey || undefined}
                onChange={(key) => setActiveKey(key)}
                addIcon="+"
                items={items}
            />
            <Modal
                title="Rename Tab"
                open={isEditing}
                onOk={handleRenameTab}
                onCancel={() => {
                    setIsEditing(false);
                    setEditingTabKey(null);
                }}
            >
                <Input
                    value={newTabLabel}
                    onChange={(e) => setNewTabLabel(e.target.value)}
                    placeholder="Enter new tab name"
                />
            </Modal>
            <DeleteModal
                showModal={showDeleteModal}
                content={
                    deleteFunction === 'clearAll' ? 'Are you sure you want to delete all functionality conditions?' :
                    deleteFunction === 'clearActiveTab' ? `Are you sure you want to delete all data for functionality ${selectedTabName || activeKey}?` :
                    `Are you sure you want to delete functionality ${selectedTabName}?`
                }
                onDelete={() => {
                    switch (deleteFunction) {
                        case 'clearAll':
                            onChange({});
                            setActiveKey(null);
                            break;
                        case 'clearActiveTab':
                            if (activeKey) {
                                onChange({ ...formData, [activeKey]: {} });
                            }
                            break;
                        case 'deleteTab':
                            if (selectedTabName) removeTab(selectedTabName);
                            break;
                        default:
                            break;
                    }
                    setShowDeleteModal(false);
                    setSelectedTabName(null);
                    setDeleteFunction(null);
                }}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedTabName(null);
                    setDeleteFunction(null);
                }}
            />
        </>
    );
}
